import AppStore from '@v4/store/appStore'
import PageTemplateData from '@v4/utils/data/PageTemplateData'
import React, { useEffect, useMemo, useState } from 'react'
import { useSectionIndex } from '@v4/components/common/SectionItem/SectionItem.context'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'

export const useMainContainer = ({ relatedSections = [undefined, undefined], ...rootProps }) => {
	const minMarginTop = AppStore.useStoreState(state => state.ui.sectionHeights[relatedSections[0]])
	const minMarginBottom = AppStore.useStoreState(state => state.ui.sectionHeights[relatedSections[1]])
	const sectionIndex = useSectionIndex()
	const ref = React.useRef()
	const isLargePage = PageBuilderSelector.useIsLargePage()
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const sectionLimit = +(import.meta.env.VITE_RENDERED_SECTION_COUNT || 0)
	const dynamicVisibility = isBuilderMode && isLargePage
	const alwaysVisible = (window?.parent !== window && !isBuilderMode) || !sectionLimit
	const [contentHeight, setContentHeight] = useState(null)
	const [visible, setVisible] = useState(() => {
		return dynamicVisibility ? false : alwaysVisible || sectionIndex < sectionLimit
	})

	useEffect(() => {
		if ((!dynamicVisibility && visible) || !ref.current) {
			return undefined
		}
		const io = new IntersectionObserver(
			entries => {
				const {
					boundingClientRect: { height },
					isIntersecting,
				} = entries[0]
				if (dynamicVisibility) {
					setContentHeight(height)
					setVisible(isIntersecting)
				} else if (isIntersecting) {
					setVisible(true)
				}
			},
			{
				rootMargin: dynamicVisibility ? '1000px' : '400px',
			},
		)
		io.observe(ref.current)
		return () => io.disconnect()
	}, [dynamicVisibility, isBuilderMode, visible])

	const margins = useMemo(
		() => ({
			top: minMarginTop || 0,
			bottom: minMarginBottom || 0,
		}),
		[minMarginBottom, minMarginTop],
	)

	return [
		{
			visible,
			contentHeight: dynamicVisibility ? contentHeight : null,
			minMarginTop: relatedSections[0] === PageTemplateData.SORT_GROUPS.HEADER ? minMarginTop : null,
			minMarginBottom: relatedSections[1] === PageTemplateData.SORT_GROUPS.FOOTER ? minMarginBottom : null,
			rootProps,
			margins,
			ref,
		},
	]
}
